'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, VIDU_STREAM_FEATURE_LINKS } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { LANGUAGE_CONFIG } from '@/i18n/config';
import { useLocale } from '@/i18n/routing';
import {
  FEATURE_CARD_VIDEOS,
  POPULAR_CASES,
  S15_CARD1_VIDEO,
  S15_CARD2_VIDEO,
  S15_CARD3_VIDEO,
  S15_CARD4_VIDEO,
  S15B_CARD1_VIDEO,
  S15B_CARD2_VIDEO,
  S15B_CARD3_VIDEO,
  S15B_CARD4_VIDEO,
} from '../../_constants/s/media';
import { BODY, SECTION_HEADING, SUBHEAD } from '../../_constants/s/typography';
import { HeroShader } from './HeroShader';
import { QuestionSection } from './QuestionSection';
import { ViduStreamFooter } from './ViduStreamFooter';

const FEATURE_KEYS = ['f1', 'f2', 'f3', 'f4'] as const;

/** Vidu S capability cards, in render order. Each shows a hover-to-play demo
 *  clip, paused on its poster by default. */
const S15_CARDS = [
  { key: 'c1', video: S15_CARD1_VIDEO },
  { key: 'c2', video: S15_CARD2_VIDEO },
  { key: 'c3', video: S15_CARD3_VIDEO },
  { key: 'c4', video: S15_CARD4_VIDEO },
] as const;

/** Editing 能做什么（第二段）的卡片：四张全用专属演示视频。 */
const S15B_CARDS = [
  { key: 'c1', video: S15B_CARD1_VIDEO },
  { key: 'c2', video: S15B_CARD2_VIDEO },
  { key: 'c3', video: S15B_CARD3_VIDEO },
  { key: 'c4', video: S15B_CARD4_VIDEO },
] as const;

// The opening kick: a small downward nudge animates the frame this far in one
// motion, after which scroll position takes over. Gives the reveal momentum
// without letting a timed animation outlive the scroll that started it.
const REVEAL_MS = 420;
const KICK_P = 0.45;

// 每张能力卡「使用说明」指向飞书 wiki，索引与 FEATURE_KEYS 前两项（f1 / f2）对应。
const FEATURE_TRY_ROUTES = ['/vidu-stream/avatar', '/vidu-stream/editing?mode=character-replacement'] as const;

const FEATURE_LINKS = [VIDU_STREAM_FEATURE_LINKS.interaction, VIDU_STREAM_FEATURE_LINKS.editing] as const;

/** Stagger delay bucket for `data-reveal-delay`; the stylesheet defines 0–5. */
const revealDelay = (i: number) => String(Math.min(i, 5));

/** Deterministic 0~1 pseudo-random from an integer seed (stable across SSR/CSR,
 *  so the per-char stagger doesn't cause hydration mismatch). */
const seededUnit = (n: number) => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/** Split the hero title into per-character spans, each with a random-looking
 *  delay + initial scale so characters appear out of order and zoom into place
 *  without any positional shift. Spaces stay plain (breakable) text so wrapping
 *  still works. */
const renderHeroChars = (title: string) => {
  let charIndex = 0;
  const tokens = title.match(/[A-Za-z0-9]+(?:[-'][A-Za-z0-9]+)*(?:[,:;.!?]+)?|\s+|[^A-Za-z0-9\s]/g) ?? [];
  return tokens.map((word) => {
    if (/^\s+$/.test(word)) {
      // 保留可断行的空格，不做动画。
      return word;
    }

    const isAsciiWord = /^[A-Za-z0-9]+(?:[-'][A-Za-z0-9]+)*(?:[,:;.!?]+)?$/.test(word);
    if (!isAsciiWord) {
      const currentIndex = charIndex;
      charIndex += 1;
      const r1 = seededUnit(currentIndex + 1);
      const r2 = seededUnit(currentIndex * 7 + 3);
      return (
        <span
          key={`${word}-${charIndex}`}
          className="hero-char"
          style={{
            animationDelay: `${Math.round(r1 * 620)}ms`,
            ['--hero-scale' as string]: (1.15 + r2 * 0.5).toFixed(3),
          }}
        >
          {word}
        </span>
      );
    }

    return (
      // 单词作为不可拆分整体，避免逐字符 inline-block 导致英文单词被拆行。
      <span key={`${word}-${charIndex}`} className="hero-word">
        {Array.from(word).map((ch) => {
          const r1 = seededUnit(charIndex + 1);
          const r2 = seededUnit(charIndex * 7 + 3);
          const delay = Math.round(r1 * 620); // 0~620ms 乱序登场
          const scale = (1.15 + r2 * 0.5).toFixed(3); // 1.15~1.65 初始缩放，幅度不一
          const currentIndex = charIndex;
          charIndex += 1;
          return (
            <span
              key={currentIndex}
              className="hero-char"
              style={{ animationDelay: `${delay}ms`, ['--hero-scale' as string]: scale }}
            >
              {ch}
            </span>
          );
        })}
      </span>
    );
  });
};

const S_PAGE_BG = '#f5f6f8';

/**
 * 落地页把 html/body 画布刷成亮色（弹性滚动时浏览器绘制的是画布，光给容器上色会露出黑边）。
 *
 * 必须用「挂载设置 + 卸载还原」的副作用，不能写在 JSX 的 <style> 里：
 * App Router 会把渲染出的 <style> 提升到 <head>，跨路由导航后规则可能残留，
 * 加上原先那两条还带 !important，会把 globals.css 的 body 深色底一直压住——
 * 表现就是从本页跳去 /assets/liverecords 等页面后底色变白。
 */
function useLightPageCanvas() {
  useEffect(() => {
    const { documentElement: html, body } = document;
    const prevHtml = html.style.background;
    const prevBody = body.style.background;

    html.style.background = S_PAGE_BG;
    body.style.background = S_PAGE_BG;

    return () => {
      html.style.background = prevHtml;
      body.style.background = prevBody;
    };
  }, []);
}

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>('[data-reveal]');

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        }
      },
      {
        // Pull the trigger line up off the bottom edge. Without this a block
        // fires the moment a sliver crosses into view and the animation is
        // already over by the time it is actually on screen.
        rootMargin: '0px 0px -32% 0px',
        threshold: 0.01,
      },
    );

    // Anything already on screen at mount (the hero) reveals right away rather
    // than waiting for the trigger line, which sits below it.
    for (const el of Array.from(els)) {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-visible');
        continue;
      }
      io.observe(el);
    }

    return () => io.disconnect();
  }, []);
}

export const ViduS: React.FC = () => {
  const t = useTranslations('vidu-s');
  const locale = useLocale();
  useLightPageCanvas();
  useReveal();

  useEffect(() => {
    reportEvent('page_view', { page_name: 'vidu_s_page' });
  }, []);

  /**
   * 站内跳转的目标地址。原先用 router.push，它会自动补 locale 前缀；改成新开
   * 标签页后走的是原生导航，前缀必须自己带上——否则非默认语言下会跳回默认语言。
   * `as-needed` 模式：默认语言不加前缀（'en' / 'zh'）。
   */
  const withLocale = useCallback(
    (path: string) => (locale === LANGUAGE_CONFIG.default ? path : `/${locale}${path}`),
    [locale],
  );

  const handleTryNow = (href: (typeof FEATURE_TRY_ROUTES)[number], feature: (typeof FEATURE_KEYS)[number]) => {
    reportEvent('element_click', { element_name: `vidu-s-feature-${feature}-try-now`, page: 'vidu-s' });
    window.open(withLocale(href), '_blank', 'noopener,noreferrer');
  };

  const handleFeatureCardClick = (
    event: React.MouseEvent<HTMLDivElement>,
    href: (typeof FEATURE_TRY_ROUTES)[number],
    feature: (typeof FEATURE_KEYS)[number],
  ) => {
    // Keep the API/usage links and the explicit CTA in control of their own navigation.
    if ((event.target as HTMLElement).closest('a,button')) return;
    handleTryNow(href, feature);
  };

  // 能力卡背景视频：默认停在 poster 封面，hover 起播（先强制静音，避免带声被浏览器拦下），
  // 离开暂停并回到首帧，重新露出封面帧。play() 被拦时 reject，吞掉以免未处理 rejection。
  const handleFeatureEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const video = e.currentTarget.querySelector<HTMLVideoElement>('[data-feature-video]');
    if (!video) return;
    video.muted = true;
    video.play().catch(() => {});
  }, []);

  const handleFeatureLeave = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const video = e.currentTarget.querySelector<HTMLVideoElement>('[data-feature-video]');
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  }, []);

  // ── Latest carousel ──
  // 桌面端继续用 translateX 轨道，避免横向滚动容器吞掉纵向滚轮；H5 则使用原生
  // 横向滚动与 snap，让手指滑动保持浏览器原生手感。两端都保留左右翻页按钮。
  const newsCarouselRef = useRef<HTMLDivElement>(null);
  const newsViewportRef = useRef<HTMLDivElement>(null);
  const newsTrackRef = useRef<HTMLDivElement>(null);
  const hasInitializedMobileNewsRef = useRef(false);
  const [newsOffset, setNewsOffset] = useState(0);
  const [canScrollNewsLeft, setCanScrollNewsLeft] = useState(false);
  const [canScrollNewsRight, setCanScrollNewsRight] = useState(false);

  const getNewsMaxOffset = useCallback((isNativeScroll = false) => {
    const viewport = newsViewportRef.current;
    const track = newsTrackRef.current;
    if (!viewport || !track) return 0;
    if (isNativeScroll) {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }
    const viewportStyle = window.getComputedStyle(viewport);
    const paddingLeft = Number.parseFloat(viewportStyle.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(viewportStyle.paddingRight) || 0;
    const viewportContentWidth = viewport.clientWidth - paddingLeft - paddingRight;
    return Math.max(0, track.scrollWidth - viewportContentWidth);
  }, []);

  const syncNewsButtons = useCallback(
    (offset: number, isNativeScroll = false) => {
      const max = getNewsMaxOffset(isNativeScroll);
      setCanScrollNewsLeft(offset > 1);
      setCanScrollNewsRight(offset < max - 1);
    },
    [getNewsMaxOffset],
  );

  const scrollNews = useCallback(
    (direction: 1 | -1) => {
      const viewport = newsViewportRef.current;
      const track = newsTrackRef.current;
      if (!viewport || !track) return;
      if (window.matchMedia('(max-width: 767px)').matches) {
        viewport.scrollBy({ left: direction * viewport.clientWidth, behavior: 'smooth' });
        return;
      }
      const card = track.querySelector<HTMLElement>('[data-news-card]');
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const stride = card ? card.offsetWidth + gap : viewport.clientWidth;
      setNewsOffset((prev) => {
        const max = getNewsMaxOffset();
        const next = Math.min(Math.max(prev + direction * stride, 0), max);
        syncNewsButtons(next);
        return next;
      });
    },
    [getNewsMaxOffset, syncNewsButtons],
  );

  const handleNewsScroll = useCallback(() => {
    const viewport = newsViewportRef.current;
    if (!viewport || !window.matchMedia('(max-width: 767px)').matches) return;
    syncNewsButtons(viewport.scrollLeft, true);
  }, [syncNewsButtons]);

  // 案例卡默认停在封面、不自动播放：鼠标进卡片才起播并**开声**，离开立即暂停、
  // 静音并回到首帧。多条视频同时循环会一直占着解码器、滚动跟着掉帧，所以只让 hover
  // 的那一张在播。play() 在浏览器拦下播放时会 reject，仍要吞掉以免抛未处理的
  // rejection。带声播放需用户手势触发（此前滚动/点击进到本屏已算交互），故进场 unmute。
  const handleNewsEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const video = e.currentTarget.querySelector<HTMLVideoElement>('[data-output-video]');
    if (!video) return;
    // 先静音起播（浏览器总允许静音播放），成功后再解除静音；hover 不算“用户激活”，
    // 直接带声 play() 会被拦下 reject，视频就停着不动，所以不能一上来就 unmute。
    video
      .play()
      .then(() => {
        video.muted = false;
      })
      .catch(() => {});
  }, []);

  const handleNewsLeave = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const video = e.currentTarget.querySelector<HTMLVideoElement>('[data-output-video]');
    if (!video) return;
    // 只暂停+静音，不回首帧：再次 hover 时从当前进度接着放，而不是重头开始。
    video.pause();
    video.muted = true;
  }, []);

  useEffect(() => {
    const viewport = newsViewportRef.current;
    const track = newsTrackRef.current;
    if (!viewport || !track) return;
    // 尺寸变化时同步当前模式的按钮显隐；从 H5 切回桌面时清掉原生滚动位移，
    // 桌面仍只使用 translateX 轨道。
    const sync = () => {
      const carousel = newsCarouselRef.current;
      const media = track.querySelector<HTMLElement>('[data-news-media]');
      if (carousel && media) {
        const carouselRect = carousel.getBoundingClientRect();
        const mediaRect = media.getBoundingClientRect();
        carousel.style.setProperty('--news-arrow-top', `${mediaRect.top - carouselRect.top + mediaRect.height / 2}px`);
      }
      if (window.matchMedia('(max-width: 767px)').matches) {
        if (!hasInitializedMobileNewsRef.current) {
          viewport.scrollLeft = 0;
          hasInitializedMobileNewsRef.current = true;
        }
        syncNewsButtons(viewport.scrollLeft, true);
        return;
      }
      hasInitializedMobileNewsRef.current = false;
      viewport.scrollLeft = 0;
      setNewsOffset((prev) => {
        const max = getNewsMaxOffset();
        const next = Math.min(prev, max);
        syncNewsButtons(next);
        return next;
      });
    };
    sync();
    const frame = window.requestAnimationFrame(sync);
    window.addEventListener('resize', sync);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    resizeObserver?.observe(track);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', sync);
      resizeObserver?.disconnect();
    };
  }, [getNewsMaxOffset, syncNewsButtons]);

  // ── Hero expansion ──
  // Asymmetric on purpose. Going down, expanding is a one-shot reveal: it fires
  // on the first hint of downward intent and plays to completion, so it reads as
  // a deliberate transition. Coming back up, the collapse is scroll-linked —
  // the user is steering back toward the hero and expects the frame to follow
  // their finger, and a fixed-duration animation there would either finish
  // before they arrive or keep running after they stop.
  // Everything is driven from the section: it carries both --p and data-mode,
  // which the stage and the cue inherit through CSS.
  const heroSectionRef = useRef<HTMLElement>(null);
  const heroCueRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const section = heroSectionRef.current;
    const cue = heroCueRef.current;
    if (!section || !cue) return;

    // Scroll distance the expand/collapse is spread over. It has to end well
    // before the next section reaches the viewport, so that from there on the
    // frame is rock steady; but too short and a fast flick clears it inside one
    // frame, which lands as a visible snap instead of a motion.
    const runwayPx = () => Math.round(window.innerHeight * 0.5);

    let current = 0;
    const setP = (p: number) => {
      current = p;
      section.style.setProperty('--p', String(p));
      cue.dataset.hidden = p > 0.5 ? 'true' : 'false';
    };
    // Mode lives on the section so the stage and the cue both key off it.
    const setMode = (mode: 'timed' | 'linked') => {
      if (section.dataset.mode !== mode) section.dataset.mode = mode;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setMode('linked');
      setP(1);
      return;
    }

    // Both directions are driven by scroll position through the same runway, for
    // one reason: a fixed-duration reveal can fall behind a fast scroll, and its
    // tail then keeps growing the frame underneath the section that has already
    // arrived — which is exactly what reads as a jump. Tying --p to position
    // makes that impossible; the frame is full bleed by the time the runway ends,
    // at any scroll speed.
    //
    // Only the *first* few pixels get a timed kick, so a small nudge still plays
    // as a deliberate reveal rather than a 20px sliver of movement.
    setMode('linked');
    setP(Math.min(1, window.scrollY / runwayPx()));

    let kickTimer: ReturnType<typeof setTimeout> | undefined;
    let kicking = false;
    let lastY = window.scrollY;

    // Written synchronously rather than inside requestAnimationFrame. Scroll
    // events are already throttled to the frame rate, and deferring to the next
    // frame leaves the frame one tick behind the scroll position — on a hard
    // flick that stale frame is what shows up as a snap.
    const onScroll = () => {
      const p = Math.min(1, window.scrollY / runwayPx());
      // While the opening kick is animating, scroll only takes over once it has
      // travelled past where the kick is heading. Otherwise the kick would be
      // cancelled by the very scroll event that triggered it.
      if (kicking) {
        if (p < KICK_P) return;
        kicking = false;
        clearTimeout(kickTimer);
        setMode('linked');
      }
      // The kick can leave the frame ahead of where scroll alone would put it.
      // Let position catch up rather than yanking the frame backwards: the value
      // may only rise, until the user actually scrolls up past it.
      setP(p > current || window.scrollY < lastY ? p : current);
      lastY = window.scrollY;
    };

    // A small downward nudge from rest animates straight to KICK_P, so the reveal
    // starts with visible momentum instead of tracking a few pixels of scroll.
    const kick = () => {
      if (kicking) return;
      const p = Math.min(1, window.scrollY / runwayPx());
      if (p >= KICK_P) return; // already further along than the kick would go
      kicking = true;
      setMode('timed');
      setP(KICK_P);
      kickTimer = setTimeout(() => {
        kicking = false;
        // Do NOT resync to the scroll position here. If the user nudged once and
        // stopped, their position maps to less than KICK_P, and snapping back to
        // it would undo the reveal that just played. Stay where the kick landed;
        // the next scroll in either direction takes over from there.
        setMode('linked');
      }, REVEAL_MS);
    };

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY > 0) kick();
    };
    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (touchStartY - (e.touches[0]?.clientY ?? 0) > 8) kick();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (['ArrowDown', 'PageDown', 'End', ' '].includes(e.key)) kick();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onScroll);
      clearTimeout(kickTimer);
    };
  }, []);
  // 首屏不再是「下滚展开铺满」的舞台：高度直接按内容撑起（标题 + 按钮），
  // 因此原先那套 --p / data-mode / kick / scroll-linked collapse 全部移除。

  return (
    <>
      <style>{`
        /* 页面固定使用亮色样式；媒体卡及其白色叠字仍保留深色表面。 */
        :root {
          --s-page-bg: ${S_PAGE_BG};
        }
        /* html/body 的画布底色改由 useLightPageCanvas 在挂载期设置、卸载时还原：
           写在内联样式里会被提升到 head 后残留，把别的路由的深色底也压成白色。 */
        .vidu-s-page {
          background: var(--s-page-bg) !important;
        }
        @media (max-width: 767px) {
          .news-track {
            transform: none !important;
            transition: none !important;
          }
        }

        /* Hero. 辉光波背景铺满，标题居中叠在上面，header 浮在其上。
           高度按内容撑起（标题 + 按钮 + 上下内边距）。 */
        .hero-section {
          min-height: 0;
        }
        @media (min-width: 768px) and (max-height: 800px) {
          .hero-title-compact {
            font-size: 36px;
          }
        }
        /* 首屏分组（hero + 两张模型卡）：header 是 fixed 浮层，分组从页面顶部
           起算、高度锁成 100svh，保证标题、「立即体验」都落在第一屏内。
           卡片区块的基准高度按 16:9 估算；视口不够时整块收缩，媒体被压矮，
           文案行 flex-shrink: 0，按钮不被裁切。多余高度给 hero。 */
        @media (min-width: 768px) {
          .hero-fold {
            display: flex;
            flex-direction: column;
            height: 100svh;
            box-sizing: border-box;
            /* 大屏把「立即体验」从视口底边托起来，避免贴边。 */
            padding-bottom: clamp(32px, 5.5svh, 80px);
          }
          .hero-fold > .hero-section {
            flex: 1 1 auto;
            min-height: min-content;
          }
          .hero-fold > .hero-section + * {
            display: flex;
            flex: 0 1 auto;
            flex-basis: calc((min(100vw, 1920px) * 0.91 - 44px) / 2 * 9 / 16 + 160px);
            flex-direction: column;
            min-height: 0;
            overflow: hidden;
          }
          .hero-fold .hero-copy {
            padding-top: clamp(88px, calc(100svh * 0.1), 140px);
            padding-bottom: clamp(4px, calc(100svh * 0.025), 32px);
          }
          .hero-fold .hero-copy > div {
            margin-top: clamp(10px, calc(100svh * 0.03), 44px);
          }
          .hero-fold .vidu-s-intro {
            display: flex;
            flex: 1 1 auto;
            min-height: 0;
            flex-direction: column;
            padding-top: clamp(4px, calc(100svh * 0.012), 16px);
            padding-bottom: clamp(8px, calc(100svh * 0.015), 16px);
          }
          .hero-fold .feature-card-grid {
            flex: 1 1 auto;
            min-height: 0;
            align-items: stretch;
            /* 两列共享「媒体 + 文案」两行，避免 2/3 行文案把左右图撑成不同高度。
               行间距必须为 0，图文间距只走文案的 margin-top，否则会叠上 gap-11。 */
            grid-template-rows: minmax(0, 1fr) auto;
            row-gap: 0;
          }
          .hero-fold .feature-card {
            display: grid;
            grid-row: span 2;
            grid-template-rows: subgrid;
            min-height: 0;
          }
          .hero-fold .feature-card-media {
            width: 100%;
            min-height: 0;
            aspect-ratio: auto;
          }
          .hero-fold .feature-card-copy {
            align-self: start;
            width: 100%;
          }
        }
        @media (min-width: 768px) and (max-height: 920px) {
          .hero-title:not(.hero-title-compact) {
            font-size: 48px;
          }
          .hero-fold .feature-card-copy {
            margin-top: 12px;
          }
        }
        /* 标题：固定深色前景。逐字淡入，见 .hero-char。 */
        .hero-title {
          color: #121826;
        }
        .hero-word {
          white-space: nowrap;
        }
        /* 每个字单独登场：只做缩放（+模糊+淡入），不产生任何位移，位置自始至终不变。
           延迟与初始缩放都按字随机（在 JSX 里算好塞进内联样式），乱序登场、幅度不一。 */
        .hero-char {
          display: inline-block;
          opacity: 0;
          animation: hero-char-in 760ms cubic-bezier(0.33,0,0.2,1) both;
        }
        @keyframes hero-char-in {
          from { opacity: 0; filter: blur(12px); transform: scale(var(--hero-scale,1.35)); }
          to   { opacity: 1; filter: blur(0);    transform: scale(1); }
        }

        /* Scroll cue fades out in step with the expansion. */
        .hero-cue { opacity: calc(1 - var(--p, 0)); }
        .hero-cue[data-hidden="true"] { pointer-events: none; }
        .hero-cue-arrow { animation: hero-cue-bob 1.8s ease-in-out infinite; }
        @keyframes hero-cue-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-char { animation: none; opacity: 1; filter: none; transform: none; }
        }

        [data-reveal] {
          opacity: 0;
          transform: translateY(64px);
          transition: opacity 0.9s cubic-bezier(0.25,0,0,1),
                      transform 0.9s cubic-bezier(0.25,0,0,1);
          will-change: opacity, transform;
        }
        /* News cards travel further still, so the motion reads as each card
           rising into place as it scrolls in. */
        [data-reveal="rise"] {
          transform: translateY(96px);
          transition-duration: 1s;
        }
        [data-reveal].is-visible {
          opacity: 1;
          transform: none;
          will-change: auto;
        }
        [data-reveal][data-reveal-delay="1"] { transition-delay: 90ms; }
        [data-reveal][data-reveal-delay="2"] { transition-delay: 180ms; }
        [data-reveal][data-reveal-delay="3"] { transition-delay: 270ms; }
        [data-reveal][data-reveal-delay="4"] { transition-delay: 360ms; }
        [data-reveal][data-reveal-delay="5"] { transition-delay: 450ms; }
        @media (prefers-reduced-motion: reduce) {
          [data-reveal] {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="vidu-s-page min-h-screen bg-[var(--s-page-bg)] text-[#121826]">
        {/* 顶部导航由 page.tsx 里的 ViduStreamHeader 提供（含 tab 切换），
            本组件不再自带 header —— 首屏的顶部内缩量对齐它的 72px 高度。 */}

        {/* ── 首屏分组：hero + 两张模型卡 ──
            ≥768px 时由 .hero-fold 锁成一屏高度（见上方 <style>），
            因此下面的能力卡从第二屏开始；更窄的屏不分组、按内容自然排。 */}
        <div className="hero-fold">
          {/* ── 1. Hero ── 辉光正弦波背景铺满，标题居中叠在上面。 */}
          <section className="hero-section relative flex items-center justify-center overflow-hidden">
            {/* 随鼠标起伏的流动辉光波 + 呼吸感 ASCII 纹理，铺满首屏。 */}
            <HeroShader className="pointer-events-none absolute inset-0 size-full" />

            <div className="hero-copy relative flex flex-col items-center px-8 pt-[88px] pb-4 sm:px-5 sm:pt-[240px] sm:pb-6">
              <h1
                className={cn(
                  'hero-title text-center font-semibold leading-[1.05] tracking-normal sm:text-[24px]',
                  locale === 'zh' ? 'text-[64px]' : 'hero-title-compact max-w-[20em] text-balance text-[48px]',
                )}
              >
                {renderHeroChars(t('title'))}
              </h1>
              <div className="mt-11 flex flex-wrap justify-center gap-3 sm:mt-8 sm:gap-2">
                <a
                  href={`${API_URL}/vidu-stream/doc`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => reportEvent('element_click', { element_name: 'vidu-s-api', page: 'vidu-s' })}
                  className="inline-flex h-11 min-w-[132px] cursor-pointer items-center justify-center rounded-full bg-[#121826] px-7 font-medium text-[14px] text-white transition-opacity hover:opacity-90 sm:h-9 sm:min-w-[112px] sm:px-5 sm:text-[13px]"
                >
                  {t('features.apiCta')}
                </a>
                <a
                  href={VIDU_STREAM_FEATURE_LINKS.overview.usage}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => reportEvent('element_click', { element_name: 'vidu-s-usage', page: 'vidu-s' })}
                  className="inline-flex h-11 min-w-[132px] cursor-pointer items-center justify-center rounded-full bg-[#121826]/10 px-7 font-medium text-[#121826] text-[14px] backdrop-blur-sm transition-colors hover:bg-[#121826]/20 sm:h-9 sm:min-w-[112px] sm:px-5 sm:text-[13px]"
                >
                  {t('features.usage')}
                </a>
              </div>
            </div>
          </section>

          {/* ── 2. Vidu S 模型 ── the two model cards, under a section heading so
            this block is titled the same way as the ones below it.
            容器与内边距以下方各区块为准（mx-auto max-w-[1920px] + px-[4.5%] sm:px-6），
            整页左边缘因此统一；代价是移动端 logo 不再与卡片左边重合
            （header 内层是 px-8，比这里多 8px）。 */}
          <div className="mx-auto w-full max-w-[1920px]">
            <section id="vidu-s-intro" className="vidu-s-intro bg-[var(--s-page-bg)] px-[4.5%] py-4 sm:px-6 sm:py-16">
              <div className="feature-card-grid grid grid-cols-2 gap-x-11 gap-y-0 sm:grid-cols-1 sm:gap-8">
                {FEATURE_KEYS.slice(0, 2).map((key, i) => (
                  <div key={key} className="feature-card" data-reveal data-reveal-delay={revealDelay(i)}>
                    {/* Tile: artwork behind, model name + links overlaid low on the
                      card. `group` so hovering anywhere zooms the art.
                      16/9。宽度由父容器（grid 列）决定，不做 max-height 限制 ——
                      空间不足时整组往下滚，不压卡片。 */}
                    <div
                      className="feature-card-media group relative aspect-[16/9] cursor-pointer overflow-hidden rounded-[20px] bg-[#141414]"
                      onClick={(event) => handleFeatureCardClick(event, FEATURE_TRY_ROUTES[i], key)}
                      onMouseEnter={handleFeatureEnter}
                      onMouseLeave={handleFeatureLeave}
                    >
                      <video
                        data-feature-video
                        src={FEATURE_CARD_VIDEOS[i].src}
                        poster={FEATURE_CARD_VIDEOS[i].poster}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                      />
                      <div className="absolute inset-0 bg-black/[0.12]" />
                      <div className="-translate-y-1/2 absolute inset-x-0 top-[64%] flex flex-col items-center">
                        <p className="px-6 text-center font-semibold text-[50px] text-white leading-[1.15] tracking-[-1px] sm:text-[34px]">
                          {(() => {
                            const title = t(`features.${key}.mediaTitle`);
                            const idx = title.lastIndexOf('-');
                            if (idx === -1) return title;
                            return (
                              <>
                                {title.slice(0, idx + 1)}
                                <span className="font-normal font-serif italic">{title.slice(idx + 1)}</span>
                              </>
                            );
                          })()}
                        </p>
                        <div className="mt-5 flex items-center gap-2 sm:mt-4">
                          <a
                            href={FEATURE_LINKS[i].api}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              reportEvent('element_click', {
                                element_name: `vidu-s-feature-${key}-api`,
                                page: 'vidu-s',
                              })
                            }
                            className="inline-flex h-12 w-[140px] items-center justify-center rounded-full border border-white/15 bg-white/[0.08] px-6 text-[16px] text-white/85 leading-[1.45] backdrop-blur-md transition-colors hover:bg-white hover:text-[#121826] sm:h-10 sm:w-[116px] sm:px-4"
                          >
                            {t('features.api')}
                          </a>
                          <a
                            href={FEATURE_LINKS[i].usage}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              reportEvent('element_click', {
                                element_name: `vidu-s-feature-${key}-usage`,
                                page: 'vidu-s',
                              })
                            }
                            className="inline-flex h-12 w-[140px] items-center justify-center rounded-full border border-white/15 bg-white/[0.08] px-6 text-[16px] text-white/85 leading-[1.45] backdrop-blur-md transition-colors hover:bg-white hover:text-[#121826] sm:h-10 sm:w-[116px] sm:px-4"
                          >
                            {t('features.usage')}
                          </a>
                        </div>
                      </div>
                    </div>
                    {/* Below the tile: name + one-line description on the left, a
                      solid「立即体验」CTA pinned to the right. */}
                    <div className="feature-card-copy mt-6 flex items-center gap-4 px-6 sm:mt-4 sm:gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-medium ${SUBHEAD}`}>{t(`features.${key}.title`)}</h3>
                        <p className={`mt-2 font-normal text-[#121826]/45 ${BODY}`}>
                          {t(`features.${key}.description`)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleTryNow(FEATURE_TRY_ROUTES[i], key)}
                        className="h-11 shrink-0 cursor-pointer rounded-full bg-[#121826] px-6 font-medium text-[14px] text-white transition-opacity hover:opacity-90 sm:h-9 sm:px-5 sm:text-[13px]"
                      >
                        {t('buttons.tryNow')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {/* Safe area: 其余区块装在居中的 1920px 列里。 */}
        <div className="mx-auto w-full max-w-[1920px]">
          {/* ── 3. Vidu S capabilities ── four cards in a row */}
          <section className="mt-10 bg-[var(--s-page-bg)] px-[4.5%] py-20 sm:px-6 sm:py-14">
            <h2 data-reveal className={cn(SECTION_HEADING, 'tracking-normal sm:tracking-normal')}>
              {t('s15.sectionTitle')}
            </h2>
            <div className="grid grid-cols-2 gap-x-11 gap-y-[60px] pt-8 sm:grid-cols-1 sm:gap-6 sm:pt-6">
              {S15_CARDS.map(({ key, video }, i) => (
                <div key={key} data-reveal data-reveal-delay={revealDelay(i)}>
                  {/* Demo clip on a flat grey panel: rests on its poster and plays
                      on hover (same handlers as the model cards). */}
                  <div
                    className="group relative flex aspect-video items-center justify-center overflow-hidden rounded-[20px] bg-[#e7eaf0] sm:rounded-[16px]"
                    onMouseEnter={handleFeatureEnter}
                    onMouseLeave={handleFeatureLeave}
                  >
                    <video
                      data-feature-video
                      src={video.src}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      className="absolute inset-0 size-full object-cover"
                    />
                    {/* 封面帧：video 的 poster 只在首帧就绪前显示，播过之后再 reset
                        currentTime 会露出视频首帧而非封面图。这里用独立 img 盖在视频上，
                        空闲时显示封面，hover 淡出让视频透出来。 */}
                    <img
                      src={video.poster}
                      alt=""
                      aria-hidden
                      className="pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-200 group-hover:opacity-0"
                    />
                  </div>
                  <h3 className={`mt-6 px-2 font-medium sm:mt-4 ${SUBHEAD}`}>{t(`s15.${key}.title`)}</h3>
                  <p className={`mt-2 px-2 font-normal text-[#121826]/45 ${BODY}`}>{t(`s15.${key}.description`)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── 3b. Editing capabilities ── same layout, its own copy (s15b) */}
          <section className="mt-10 bg-[var(--s-page-bg)] px-[4.5%] py-20 sm:px-6 sm:py-14">
            <h2 data-reveal className={cn(SECTION_HEADING, 'tracking-normal sm:tracking-normal')}>
              {t('s15b.sectionTitle')}
            </h2>
            <div className="grid grid-cols-2 gap-x-11 gap-y-[60px] pt-8 sm:grid-cols-1 sm:gap-6 sm:pt-6">
              {S15B_CARDS.map(({ key, video }, i) => (
                <div key={key} data-reveal data-reveal-delay={revealDelay(i)}>
                  {/* Demo clip on a flat grey panel: rests on its poster and plays
                      on hover (same handlers as the model cards). */}
                  <div
                    className="group relative flex aspect-video items-center justify-center overflow-hidden rounded-[20px] bg-[#e7eaf0] sm:rounded-[16px]"
                    onMouseEnter={handleFeatureEnter}
                    onMouseLeave={handleFeatureLeave}
                  >
                    <video
                      data-feature-video
                      src={video.src}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      className="absolute inset-0 size-full object-cover"
                    />
                    {/* 封面帧：video 的 poster 只在首帧就绪前显示，播过之后再 reset
                        currentTime 会露出视频首帧而非封面图。这里用独立 img 盖在视频上，
                        空闲时显示封面，hover 淡出让视频透出来。 */}
                    <img
                      src={video.poster}
                      alt=""
                      aria-hidden
                      className="pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-200 group-hover:opacity-0"
                    />
                    {/* 8% 黑色蒙版，压暗封面/视频，提升卡片上的文字可读性。 */}
                    <div className="pointer-events-none absolute inset-0 bg-black/8" />
                  </div>
                  <h3 className={`mt-6 px-2 font-medium sm:mt-4 ${SUBHEAD}`}>{t(`s15b.${key}.title`)}</h3>
                  <p className={`mt-2 px-2 font-normal text-[#121826]/45 ${BODY}`}>{t(`s15b.${key}.description`)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── 4. 热门案例 ── a wide horizontally scrolling row of case cards.
              有输入图时「左输入 → 右视频」分栏；没有输入图时横版铺满内区，竖版 9:16 居中。
              卡片默认停在封面，hover 整卡才带声起播。 */}
          <section className="mt-10 bg-[var(--s-page-bg)] py-14 sm:py-14">
            <h2 data-reveal className={cn('px-[4.5%] sm:px-6', SECTION_HEADING, 'tracking-normal sm:tracking-normal')}>
              {t('latest.sectionTitle')}
            </h2>

            <div ref={newsCarouselRef} data-reveal="rise" className="relative sm:px-6">
              <div
                ref={newsViewportRef}
                className="hide-scrollbar select-none overflow-x-clip overflow-y-visible px-[4.5%] pb-1 sm:snap-x sm:snap-mandatory sm:overflow-x-auto sm:px-0"
                onScroll={handleNewsScroll}
              >
                <div
                  ref={newsTrackRef}
                  className="news-track flex items-center gap-9 will-change-transform sm:gap-4"
                  style={{
                    transform: `translate3d(${-newsOffset}px,0,0)`,
                    transition: 'transform 0.5s ease',
                  }}
                >
                  {POPULAR_CASES.map((popularCase, i) => {
                    const hasInput = popularCase.inputs.length > 0;

                    return (
                      <div
                        key={popularCase.id}
                        data-news-card
                        aria-label={`${t('latest.sectionTitle')} ${i + 1}`}
                        onMouseEnter={handleNewsEnter}
                        onMouseLeave={handleNewsLeave}
                        className="group flex w-[75vw] shrink-0 flex-col sm:w-full sm:snap-start"
                      >
                        {/* 有输入图：16:9 容器内左输入、右视频。无输入：横版铺满内区，竖版 9:16 居中。 */}
                        <div
                          data-news-media
                          className="relative flex aspect-video min-h-0 w-full items-center justify-center overflow-clip rounded-[24px] border border-[#d8dde7] bg-[#e7eaf0] px-4 pt-6 pb-4 transition-colors group-hover:bg-[#dde2eb] sm:rounded-[18px] sm:px-3 sm:pt-4 sm:pb-3"
                        >
                          {hasInput ? (
                            <div className="flex h-full min-h-0 w-full min-w-0 items-center justify-center gap-3 overflow-clip sm:gap-2">
                              <div className="flex h-full w-fit min-w-0 max-w-[180px] flex-none flex-col items-center justify-center gap-8 overflow-visible sm:max-w-[96px]">
                                {popularCase.inputs.map((input, inputIndex) => (
                                  <div
                                    key={`${input.src}-${inputIndex}`}
                                    className="flex min-h-0 w-fit max-w-full flex-1 flex-col items-center justify-center gap-3 sm:gap-1.5"
                                    style={{
                                      maxHeight: `min(33.333333%, calc((100% - ${(popularCase.inputs.length - 1) * 12}px) / ${popularCase.inputs.length}))`,
                                    }}
                                  >
                                    {input.type === 'image' ? (
                                      <img
                                        src={input.src}
                                        alt=""
                                        aria-hidden="true"
                                        className="block h-auto max-h-[calc(100%_-_22px)] w-auto max-w-full rounded-[16px] sm:max-h-[calc(100%_-_18px)] sm:rounded-[10px]"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <video
                                        className="block h-auto max-h-[calc(100%_-_22px)] w-auto max-w-full rounded-[16px] sm:max-h-[calc(100%_-_18px)] sm:rounded-[10px]"
                                        src={input.src}
                                        poster={input.poster}
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                      />
                                    )}
                                    <span className="flex-shrink-0 text-center font-medium text-[12px] text-black/55 leading-none sm:text-[10px]">
                                      {t(`latest.${input.type === 'image' ? 'inputImage' : 'inputVideo'}`)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div className="flex h-full max-h-full min-h-0 min-w-0 shrink-0 items-center justify-center self-center overflow-clip">
                                <video
                                  data-output-video
                                  className="block h-auto max-h-full w-auto max-w-full rounded-[20px] sm:rounded-[12px]"
                                  src={popularCase.output.src}
                                  poster={popularCase.output.poster}
                                  muted
                                  loop
                                  playsInline
                                  preload="metadata"
                                />
                              </div>
                            </div>
                          ) : (
                            <video
                              data-output-video
                              className={
                                popularCase.orientation === 'portrait'
                                  ? 'aspect-[9/16] h-full w-auto rounded-[20px] object-cover sm:rounded-[12px]'
                                  : 'block size-full rounded-[20px] object-cover sm:rounded-[12px]'
                              }
                              src={popularCase.output.src}
                              poster={popularCase.output.poster}
                              muted
                              loop
                              playsInline
                              preload="metadata"
                            />
                          )}
                        </div>
                        <p className="mt-4 px-3 text-left font-normal text-[#121826] text-[16px] leading-[1.4] sm:mt-3 sm:text-[13px]">
                          {t(`latest.descriptions.${popularCase.descriptionKey}`)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Edge fades hint that there is more to either side; only shown
                  while there actually is. */}
              <div
                className={cn(
                  'pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[var(--s-page-bg)] to-transparent transition-opacity duration-300 sm:w-10',
                  canScrollNewsLeft ? 'opacity-100' : 'opacity-0',
                )}
              />
              <div
                className={cn(
                  'pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[var(--s-page-bg)] to-transparent transition-opacity duration-300 sm:w-10',
                  canScrollNewsRight ? 'opacity-100' : 'opacity-0',
                )}
              />
              {canScrollNewsLeft && (
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={() => scrollNews(-1)}
                  className="-translate-y-1/2 absolute top-[var(--news-arrow-top)] left-2.5 z-10 flex size-11 items-center justify-center rounded-full bg-[#121826]/10 text-[#121826]/80 backdrop-blur-md transition-colors hover:bg-[#121826]/20 hover:text-[#121826] sm:size-8"
                >
                  <ChevronLeft className="size-5 sm:size-4" strokeWidth={2} />
                </button>
              )}
              {canScrollNewsRight && (
                <button
                  type="button"
                  aria-label="Next"
                  onClick={() => scrollNews(1)}
                  className="-translate-y-1/2 absolute top-[var(--news-arrow-top)] right-2.5 z-10 flex size-11 items-center justify-center rounded-full bg-[#121826]/10 text-[#121826]/80 backdrop-blur-md transition-colors hover:bg-[#121826]/20 hover:text-[#121826] sm:size-8"
                >
                  <ChevronRight className="size-5 sm:size-4" strokeWidth={2} />
                </button>
              )}
            </div>
          </section>

          {/* ── 5. FAQ ── */}
          <section className="mt-10">
            <QuestionSection />
          </section>

          <ViduStreamFooter />
        </div>
      </div>
    </>
  );
};
